/**
 * MET-Based Workout Calorie Calculation
 * 
 * Formula: Calories = MET × weight_kg × duration_hours
 * 
 * MET (Metabolic Equivalent of Task) values for different workout types and intensities.
 * All values are based on scientific MET tables.
 */

/**
 * MET values lookup table
 * Based on Sprint A specification
 */
export const MET_VALUES = {
  // Strength training
  'strength-moderate': 5.5,
  'strength-heavy': 6.0,
  
  // Core and flexibility
  'core': 3.5,
  'pilates-stretch': 2.5,
  'pilates-standard': 3.2,
  'pilates-cardio': 4.6,
  'pilates': 3.2,
  'yoga-restorative': 2.3,
  'yoga-standard': 3.0,
  'yoga-power': 4.0,
  'yoga': 3.0,
  
  // High intensity
  'hiit': 8.0,
  
  // Cycling/Spin
  'spin-moderate': 6.0,
  'spin-hard': 8.0,
  'cycling-easy': 3.5,
  'cycling-moderate': 6.0,
  
  // Rowing
  'rowing-moderate': 6.0,
  'rowing-hard': 8.0,
  
  // Cardio machines
  'elliptical-moderate': 5.0,
  'elliptical-vigorous': 6.0,
  'stair-climber': 9.0,
  
  // Other activities
  'farmers-walk': 8.0,
  'bodyweight-circuits': 4.5,
  'boxing': 7.5,
  'other-workout': 5.0,
  
  // Walking (for MET-based calculation)
  'walking': 3.5,
} as const;

export type METWorkoutType = keyof typeof MET_VALUES;

// ── Deterministic Activity Keyword Router ──
// Maps specific activity phrases to their MET key and stored workout type.
// Checked BEFORE generic type inference and BEFORE AI classification.
// Ordered longest/most-specific first to prevent partial matches
// (e.g., "farmers walk" resolves before generic "walk" would).

interface InferredActivity {
  activityKey: METWorkoutType;
  workoutType: 'run' | 'walk' | 'cycle' | 'weightlifting' | 'manual';
  met: number;
}

/** Keyword → activity mapping. Order matters: specific phrases first. */
const ACTIVITY_KEYWORDS: Array<{ patterns: string[]; activityKey: METWorkoutType; workoutType: InferredActivity['workoutType'] }> = [
  // Loaded carries — must resolve before generic "walk"
  { patterns: ["farmers walk", "farmer's walk", "farmers carry", "farmer carry", "loaded carry"],
    activityKey: 'farmers-walk', workoutType: 'manual' },
  // Stair machines
  { patterns: ["stair climber", "stairclimber", "stair master", "stairmaster", "step machine", "stair machine"],
    activityKey: 'stair-climber', workoutType: 'manual' },
  // Combat
  { patterns: ["boxing", "heavy bag", "punching bag", "shadow boxing", "kickboxing"],
    activityKey: 'boxing', workoutType: 'manual' },
  // HIIT
  { patterns: ["hiit", "high intensity interval"],
    activityKey: 'hiit', workoutType: 'manual' },
  // Elliptical
  { patterns: ["elliptical", "cross trainer", "cross-trainer"],
    activityKey: 'elliptical-moderate', workoutType: 'manual' },
  // Rowing
  { patterns: ["rowing machine", "row machine", "erg", "concept2", "concept 2"],
    activityKey: 'rowing-moderate', workoutType: 'manual' },
  // Bodyweight
  { patterns: ["bodyweight circuit", "bodyweight circuits", "calisthenics"],
    activityKey: 'bodyweight-circuits', workoutType: 'manual' },
];

/**
 * Infer activity from description keywords. Returns MET key, workout type, and MET value.
 * Returns null if no specific match — caller should fall through to generic type inference.
 *
 * This is the single source of truth for keyword→activity mapping.
 * Both calorie calculation and type classification should consult this first.
 */
export function inferActivityFromDescription(description: string): InferredActivity | null {
  if (!description) return null;
  const lower = description.toLowerCase().trim();

  for (const entry of ACTIVITY_KEYWORDS) {
    for (const pattern of entry.patterns) {
      // Whole-word boundary match to prevent "row" matching "arrow"
      const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (new RegExp(`\\b${escaped}\\b`, 'i').test(lower)) {
        return {
          activityKey: entry.activityKey,
          workoutType: entry.workoutType,
          met: MET_VALUES[entry.activityKey],
        };
      }
    }
  }

  return null;
}

/**
 * Calculate calories using MET formula
 * Formula: Calories = MET × weight_kg × duration_hours
 */
export function calculateCaloriesFromMET(
  met: number,
  weightKg: number,
  durationMinutes: number,
): number {
  if (!weightKg || weightKg <= 0) {
    throw new Error('User weight must be provided and greater than 0');
  }
  if (!durationMinutes || durationMinutes <= 0) {
    throw new Error('Duration must be provided and greater than 0');
  }
  if (!met || met <= 0) {
    throw new Error('MET value must be provided and greater than 0');
  }
  const durationHours = durationMinutes / 60;
  return Math.round(met * weightKg * durationHours);
}

/**
 * Get MET value for a workout type
 */
export function getMETValue(workoutType: METWorkoutType): number {
  return MET_VALUES[workoutType];
}

/**
 * Calculate calories for a MET-based workout
 */
export function calculateMETWorkoutCalories(
  workoutType: METWorkoutType,
  weightKg: number,
  durationMinutes: number,
): number {
  const met = getMETValue(workoutType);
  return calculateCaloriesFromMET(met, weightKg, durationMinutes);
}

/**
 * Get Pilates MET based on intensity
 */
export function getPilatesMET(
  description?: string,
  intensity?: 'stretch' | 'standard' | 'cardio',
): number {
  if (intensity === 'stretch') return MET_VALUES['pilates-stretch'];
  if (intensity === 'cardio') return MET_VALUES['pilates-cardio'];
  if (intensity === 'standard') return MET_VALUES['pilates-standard'];
  
  if (description) {
    const descLower = description.toLowerCase();
    if (/\b(cardio|power|intense|sweat|elevated|hr|heart rate|high|vigorous)\b/i.test(descLower)) {
      return MET_VALUES['pilates-cardio'];
    }
    if (/\b(stretch|stretching|mobility|gentle|easy|relax|restorative)\b/i.test(descLower)) {
      return MET_VALUES['pilates-stretch'];
    }
  }
  return MET_VALUES['pilates-standard'];
}

/**
 * Get Yoga MET based on intensity
 */
export function getYogaMET(
  description?: string,
  intensity?: 'restorative' | 'standard' | 'power',
): number {
  if (intensity === 'restorative') return MET_VALUES['yoga-restorative'];
  if (intensity === 'power') return MET_VALUES['yoga-power'];
  if (intensity === 'standard') return MET_VALUES['yoga-standard'];
  
  if (description) {
    const descLower = description.toLowerCase();
    if (/\b(power|vinyasa|ashtanga|flow|dynamic|intense|sweat|elevated|hr|heart rate|high)\b/i.test(descLower)) {
      return MET_VALUES['yoga-power'];
    }
    if (/\b(restorative|yin|gentle|relax|meditation|slow|easy)\b/i.test(descLower)) {
      return MET_VALUES['yoga-restorative'];
    }
  }
  return MET_VALUES['yoga-standard'];
}

/**
 * Calculate Pilates calories with intensity awareness
 */
export function calculatePilatesCalories(
  weightKg: number,
  durationMinutes: number,
  description?: string,
  intensity?: 'stretch' | 'standard' | 'cardio',
): number {
  const met = getPilatesMET(description, intensity);
  return calculateCaloriesFromMET(met, weightKg, durationMinutes);
}

/**
 * Calculate Yoga calories with intensity awareness
 */
export function calculateYogaCalories(
  weightKg: number,
  durationMinutes: number,
  description?: string,
  intensity?: 'restorative' | 'standard' | 'power',
): number {
  const met = getYogaMET(description, intensity);
  return calculateCaloriesFromMET(met, weightKg, durationMinutes);
}

// ─── Elevation Gain Calorie Uplift ───

/**
 * Efficiency factors: metabolic cost ÷ mechanical output per activity.
 * Running is more efficient than walking; cycling is most efficient.
 */
const ELEVATION_EFFICIENCY: Record<string, number> = {
  run: 5.0,
  walk: 6.0,
  cycle: 4.0,
};

/**
 * Cap on elevation uplift relative to base calories per activity.
 * Prevents insane totals from erroneous elevation data.
 */
const ELEVATION_CAP: Record<string, number> = {
  run: 0.60,
  walk: 0.80,
  cycle: 0.50,
};

/**
 * Calculate extra calories burned from elevation gain.
 *
 * Physics: work_J = weight_kg × 9.81 × elevation_m
 *          mech_kcal = work_J / 4184
 *          extra_kcal = mech_kcal × efficiency_factor
 *
 * Capped at a percentage of base calories to guard against GPS noise.
 */
export function elevationExtraKcal(
  activity: 'run' | 'walk' | 'cycle',
  weightKg: number,
  elevationGainM: number | undefined | null,
  baseCalories: number,
): number {
  if (!elevationGainM || elevationGainM <= 0 || !weightKg || weightKg <= 0) {
    return 0;
  }

  const efficiency = ELEVATION_EFFICIENCY[activity] ?? 5.0;
  const cap = ELEVATION_CAP[activity] ?? 0.60;

  const workJoules = weightKg * 9.81 * elevationGainM;
  const mechKcal = workJoules / 4184;
  const extraKcal = Math.round(mechKcal * efficiency);

  // Cap at percentage of base calories
  const maxExtra = Math.round(baseCalories * cap);
  return Math.min(extraKcal, maxExtra);
}